// src/components/ProdMgrs/ProdMgrsYearOverYearTable.jsx
// =============================================================================
// BLOCK PMGR.YOY.0 — Year Over Year Table (Program Manager)
// Purpose:
//  - Render the shared YoY table using /api/prod-mgrs/year-over-year
//  - Normalize server row shape so YearOverYearRow never sees undefined/NaN
// =============================================================================

import React from 'react';
import PropTypes from 'prop-types';

import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Typography from '@mui/material/Typography';

import axios from '../../../axiosClient.js';
import YearOverYearHeader from '../../YearOverYearHeader.jsx';
import YearOverYearRow from '../../YearOverYearRow.jsx';

// =============================================================================
// BLOCK PMGR.YOY.1 — Normalizers
// =============================================================================

function toNum(v) {
  if (v === null || v === undefined) {
    return 0;
  }
  if (typeof v === 'number') {
    return Number.isFinite(v) ? v : 0;
  }
  if (typeof v === 'string') {
    const cleaned = v.replace(/[$,]/g, '').trim();
    if (!cleaned) {
      return 0;
    }
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function pickProductGroup(r) {
  // Common backend variants
  const v = r?.ProductGroup ?? r?.productgroup ?? r?.category ?? r?.CATEGORY ?? r?.Product_Group;
  return String(v ?? '').trim();
}

// Return the first value that is not null/undefined (0 is allowed and should win)
function firstDefined(...vals) {
  for (let i = 0; i < vals.length; i += 1) {
    const v = vals[i];
    if (v !== null && v !== undefined) {
      return v;
    }
  }
  return undefined;
}

function normalizeYoyRow(r) {
  const ProductGroup = pickProductGroup(r);

  // ---------------------------------------------------------------------------
  // BLOCK PMGR.YOY.1A — Current/Prior numeric fields
  // Notes:
  //  - Use "firstDefined" so a valid 0 is not skipped (|| would skip it).
  // ---------------------------------------------------------------------------
  const currentRaw = firstDefined(
    r?.current,
    r?.Current,
    r?.currentYear,
    r?.Year0,
    r?.NetSaleCurrent,
    r?.CurrentNetSales,
    r?.CurrentYearNetSales
  );

  const priorRaw = firstDefined(
    r?.prior,
    r?.Prior,
    r?.priorYear,
    r?.Year1,
    r?.NetSalePrior,
    r?.PriorNetSales,
    r?.PreviousYearNetSales
  );

  const current = toNum(currentRaw);
  const prior = toNum(priorRaw);

  // ---------------------------------------------------------------------------
  // BLOCK PMGR.YOY.1B — Delta and pct (NO nested ternary)
  // Notes:
  //  - If backend already provides delta/pct, use them.
  //  - Otherwise compute:
  //      delta = current - prior
  //      pct = (prior === 0) ? 0 : delta/prior
  // ---------------------------------------------------------------------------
  const hasDelta = r?.delta !== undefined || r?.Delta !== undefined || r?.Change !== undefined;
  const hasPct = r?.pct !== undefined || r?.Pct !== undefined || r?.percent !== undefined;

  let delta = 0;
  if (hasDelta) {
    delta = toNum(firstDefined(r?.delta, r?.Delta, r?.Change));
  } else {
    delta = current - prior;
  }

  let pct = 0;
  if (hasPct) {
    pct = toNum(firstDefined(r?.pct, r?.Pct, r?.percent));
  } else if (prior !== 0) {
    pct = delta / prior;
  } else {
    pct = 0;
  }

  return {
    ...r,
    ProductGroup,

    // Keep normalized aliases
    current,
    prior,
    delta,
    pct,

    // Shared YearOverYearRow expects these exact keys
    CurrentYearNetSales: current,
    PreviousYearNetSales: prior,
    Change: delta,
  };
}

// =============================================================================
// BLOCK PMGR.YOY.2 — Component
// =============================================================================

function ProdMgrsYearOverYearTable({ selectedRep, selectedYear }) {
  const [rows, setRows] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!selectedRep || !selectedYear) {
      setRows([]);
      setError(null);
      setIsLoading(false);
      return () => {};
    }

    let alive = true;

    async function load() {
      setIsLoading(true);
      setError(null);

      try {
        const { data } = await axios.get('/api/prod-mgrs/year-over-year', {
          params: { rep: selectedRep, year: selectedYear },
        });

        const raw = Array.isArray(data) ? data : [];
        const normalized = raw.map((r) => normalizeYoyRow(r)).filter((r) => r.ProductGroup); // drop empty groups

        if (!alive) {
          return;
        }
        setRows(normalized);
      } catch (e) {
        if (!alive) {
          return;
        }
        setError(e);
        setRows([]);
      } finally {
        if (alive) {
          setIsLoading(false);
        }
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, [selectedRep, selectedYear]);

  const errorMsg =
    error?.response?.data?.error || error?.message || 'Failed to load YoY comparison.';

  // =============================================================================
  // BLOCK PMGR.YOY.3 — Render
  // =============================================================================

  return (
    <Paper elevation={3} sx={{ padding: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Territory YoY comparison
      </Typography>

      {!selectedRep ? <Alert severity="info">Select a rep to view YoY comparison.</Alert> : null}
      {error ? <Alert severity="error">{errorMsg}</Alert> : null}

      {isLoading ? (
        <CircularProgress size={28} />
      ) : (
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              {/* YearOverYearHeader already returns a <TableRow> */}
              <YearOverYearHeader selectedYear={selectedYear} />
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <YearOverYearRow
                  // ✅ stable key: ProductGroup + year (no array index)
                  key={`${r.ProductGroup}-${String(selectedYear)}`}
                  yearOverYearNetSale={r}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

ProdMgrsYearOverYearTable.propTypes = {
  selectedRep: PropTypes.string.isRequired,
  selectedYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default ProdMgrsYearOverYearTable;
